import { Button } from "@package/subframe/components/Button";
import { FeatherClock, FeatherShieldCheck } from "@subframe/core";
import { Link } from "@tanstack/react-router";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import { ComplianceTaskInstanceResponseDtoStatus } from "@/generated/model";
import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import type { ComplianceInstanceListItem } from "@/hooks/use-offline-compliance-instances";
import { primaryCategory } from "@/lib/compliance-manager";
import { complianceInstancePill } from "@/lib/workspace/status-pill";

interface Props {
  instance: ComplianceInstanceListItem;
  categories: CategoryResponseDto[];
}

function formatDueDate(dueDate: string): string {
  const date = new Date(dueDate);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export function ComplianceInstanceRow({ instance, categories }: Props) {
  const overdue =
    instance.status === ComplianceTaskInstanceResponseDtoStatus.OVERDUE;
  const dueDateText = formatDueDate(instance.dueDate);
  // `definitionName` is populated by the list endpoint's projection. The
  // fallback is defensive — only triggers if the parent definition couldn't
  // be loaded server-side (rare).
  const title = instance.definitionName ?? "Compliance task";
  const category = primaryCategory(instance, categories);

  return (
    <Link
      to="/compliance/instance/$instanceId"
      params={{ instanceId: instance.id }}
      className="block hover:bg-neutral-50 transition-colors"
    >
      <div className="flex w-full items-center justify-between gap-4 rounded-sm px-4 py-3 mobile:items-start mobile:gap-3 mobile:rounded-none mobile:px-4 mobile:py-4">
        <div className="flex items-center gap-3 grow shrink-0 basis-0 max-w-[50%]">
          <TintedEntityIcon
            entity="compliance-action"
            slug={category?.slug}
            color={category?.color ?? undefined}
            size={40}
            fallbackIcon={<FeatherShieldCheck />}
            title={
              category ? `Compliance Action — ${category.name}` : undefined
            }
          />
          <div className="flex min-w-0 flex-col items-start gap-1">
            <span className="text-body-bold font-body-bold text-neutral-900 mobile:text-large-button mobile:font-large-button truncate w-full">
              {title}
            </span>
            <div className="flex items-center gap-1 mobile:gap-1.5">
              <FeatherClock
                className={`text-caption font-caption mobile:text-large-button mobile:font-large-button ${overdue ? "text-error-700" : "text-subtext-color"}`}
              />
              <span
                className={`text-caption font-caption mobile:text-body mobile:font-body ${overdue ? "text-error-700" : "text-subtext-color"}`}
              >
                Due {dueDateText}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 flex-shrink-0 mobile:flex-col mobile:items-end mobile:gap-2">
          <WorkspaceStatusBadge
            pill={complianceInstancePill(instance.status)}
          />
          <Button className="mobile:hidden" variant="neutral-primary">
            View
          </Button>
        </div>
      </div>
    </Link>
  );
}
